import { motion } from 'framer-motion';
import { AlertTriangle } from '../icons';
import type { InactiveCoach } from '../../lib/partner-dashboard';

interface InactiveCoachesAlertProps {
  coaches: InactiveCoach[];
}

function formatLastActivity(dateStr: string | null): string {
  if (!dateStr) return 'No activity recorded';
  const date = new Date(dateStr);
  const now = new Date();
  const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 60) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

export function InactiveCoachesAlert({ coaches }: InactiveCoachesAlertProps) {
  if (coaches.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-orange-10 border border-orange-40/30 rounded-2xl p-5"
    >
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 text-orange-50 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-text-md text-gray-80 font-semibold">
            {coaches.length} Coach{coaches.length !== 1 ? 'es' : ''} with No Recent Activity
          </h3>
          <p className="text-text-sm text-gray-50 mt-0.5">
            These coaches have had no completed assessments in the last 30 days
          </p>
        </div>
      </div>
      <div className="ml-8 space-y-1.5">
        {coaches.map(coach => (
          <div
            key={coach.coachId}
            className="flex items-center justify-between text-text-sm py-1"
          >
            <span className="text-gray-60 font-medium">{coach.coachName}</span>
            <span className="text-gray-40 text-text-xs">
              {formatLastActivity(coach.lastActivityDate)}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
